import * as fs from 'fs';
import * as path from 'path';

// !Running script should no longer be needed - refer to file clientMockData.ts (line 25).
/**
 * Script to update date fields in mock data JSON files
 * Usage: npx ts-node src/utils/updateMockDataDates.ts [daysOffset]
 * Example: npx ts-node src/utils/updateMockDataDates.ts -30 (for dates 30 days ago)
 * Example: npx ts-node src/utils/updateMockDataDates.ts 7 (for dates 7 days from now)
 */

interface MockDataRecord {
  id?: string;
  due_date?: string;
  creation_date?: string;
  trans_date?: string;
  verdict_date?: string;
  last_action_date?: string;
  credit_date?: string;
  details_data?: MockDataRecord[]; // For nested arrays like master/detail structures
  [key: string]: any;
}

interface MockDataFile {
  data: MockDataRecord[];
}

// Date field names that should be updated
const DATE_FIELDS = ['due_date', 'date_created', 'creation_date', 'trans_date', 'verdict_date', 'last_action_date', 'credit_date', 'update_date'];

// Mock data directories to process
const MOCK_DIRECTORIES = [
  'apps/ent/src/views/CaseManagementAGGrid/grids/mocks',
  'apps/ent/src/views/ChargebacksAGGrid/grids/mocks',
  'apps/ent/src/views/InvalidChargebacksAGGrid/grids/mocks'
];

/**
 * Format date as YYYY-MM-DD
 */
function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * Calculate new date based on existing date and offset
 */
function calculateNewDate(existingDate: string, daysOffset: number): string {
  const baseDate = new Date(existingDate);
  const newDate = new Date(baseDate);
  newDate.setDate(baseDate.getDate() + daysOffset);
  return formatDate(newDate);
}

/**
 * Calculate preview date based on current date and offset (for display purposes)
 */
function calculatePreviewDate(daysOffset: number): string {
  const currentDate = new Date();
  const newDate = new Date(currentDate);
  newDate.setDate(currentDate.getDate() + daysOffset);
  return formatDate(newDate);
}

/**
 * Check if a date string is valid and not empty
 */
function isValidDate(dateStr: string): boolean {
  if (!dateStr || dateStr.trim() === '') {
    return false;
  }
  const date = new Date(dateStr);
  return !isNaN(date.getTime());
}

/**
 * Update date fields in a single record (recursively handles nested objects and arrays)
 */
export function updateRecordDates(record: MockDataRecord, daysOffset: number): MockDataRecord {
  const updatedRecord = { ...record };
  
  // Update top-level date fields
  DATE_FIELDS.forEach(field => {
    if (updatedRecord[field] && isValidDate(updatedRecord[field] as string)) {
      updatedRecord[field] = calculateNewDate(updatedRecord[field] as string, daysOffset);
    }
  });
  
  // Recursively update nested objects and arrays
  Object.keys(updatedRecord).forEach(key => {
    const value = updatedRecord[key];
    
    if (Array.isArray(value)) {
      // Handle arrays (like details_data)
      updatedRecord[key] = value.map(item => {
        if (typeof item === 'object' && item !== null) {
          return updateRecordDates(item, daysOffset);
        }
        return item;
      });
    } else if (typeof value === 'object' && value !== null && !DATE_FIELDS.includes(key)) {
      // Handle nested objects (but skip date fields we already processed)
      updatedRecord[key] = updateRecordDates(value, daysOffset);
    }
  });
  
  return updatedRecord;
}

/**
 * Process a single JSON file
 */
function processJsonFile(filePath: string, daysOffset: number): void {
  try {
    console.log(`Processing: ${filePath}`);
    
    // Read the file
    const fileContent = fs.readFileSync(filePath, 'utf8');
    const mockData: MockDataFile = JSON.parse(fileContent);
    
    // Track updates
    let updatedCount = 0;
    
    // Update each record
    if (mockData.data && Array.isArray(mockData.data)) {
      mockData.data = mockData.data.map(record => {
        const originalRecord = { ...record };
        const updatedRecord = updateRecordDates(record, daysOffset);
        
        // Check if any dates were updated
        const hasUpdates = DATE_FIELDS.some(field => 
          originalRecord[field] !== updatedRecord[field]
        );
        
        if (hasUpdates) {
          updatedCount++;
        }
        
        return updatedRecord;
      });
    }
    
    // Write back to file with proper formatting
    fs.writeFileSync(filePath, JSON.stringify(mockData, null, 4));
    
    console.log(`  ✓ Updated ${updatedCount} records`);
    
  } catch (error) {
    console.error(`  ✗ Error processing ${filePath}:`, error);
  }
}

/**
 * Recursively find all JSON files in a directory
 */
function findJsonFiles(dirPath: string): string[] {
  const jsonFiles: string[] = [];
  
  if (!fs.existsSync(dirPath)) {
    console.warn(`Directory not found: ${dirPath}`);
    return jsonFiles;
  }
  
  const items = fs.readdirSync(dirPath);
  
  items.forEach(item => {
    const itemPath = path.join(dirPath, item);
    const stat = fs.statSync(itemPath);
    
    if (stat.isDirectory()) {
      // Recursively search subdirectories
      jsonFiles.push(...findJsonFiles(itemPath));
    } else if (stat.isFile() && item.endsWith('.json')) {
      jsonFiles.push(itemPath);
    }
  });
  
  return jsonFiles;
}

/**
 * Main function to update all mock data files
 */
function updateMockDataDates(daysOffset: number): void {
  console.log(`\n🔄 Updating mock data dates with offset: ${daysOffset} days`);
  console.log(`📅 Preview: Current date + ${daysOffset} days = ${calculatePreviewDate(daysOffset)}`);
  console.log(`📝 Note: Each existing date will have ${daysOffset} days added to it\n`);
  
  let totalFilesProcessed = 0;
  
  MOCK_DIRECTORIES.forEach(mockDir => {
    const fullPath = path.resolve(mockDir);
    console.log(`\n📁 Processing directory: ${mockDir}`);
    
    const jsonFiles = findJsonFiles(fullPath);
    
    if (jsonFiles.length === 0) {
      console.log(`  No JSON files found in ${mockDir}`);
      return;
    }
    
    jsonFiles.forEach(filePath => {
      processJsonFile(filePath, daysOffset);
      totalFilesProcessed++;
    });
  });
  
  console.log(`\n✅ Completed! Processed ${totalFilesProcessed} files total.`);
}

// Main execution
if (require.main === module) {
  const args = process.argv.slice(2);
  
  if (args.length === 0) {
    console.log('Usage: npx ts-node src/utils/updateMockDataDates.ts [daysOffset]');
    console.log('Example: npx ts-node src/utils/updateMockDataDates.ts -30 (for dates 30 days ago)');
    console.log('Example: npx ts-node src/utils/updateMockDataDates.ts 7 (for dates 7 days from now)');
    process.exit(1);
  }
  
  const daysOffset = parseInt(args[0], 10);
  
  if (isNaN(daysOffset)) {
    console.error('Error: daysOffset must be a valid number');
    process.exit(1);
  }
  
  updateMockDataDates(daysOffset);
}

// Export for use as a module
export { updateMockDataDates, calculateNewDate, isValidDate };